import { useCallback, useEffect, useState } from 'react';
import { api, ApiClientError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useToast } from '../../components/Toast';
import Modal from '../../components/Modal';
import { Badge, EmptyState, PageLoading, TextArea, TextInput } from '../../components/ui';
import Icon from '../../components/Icon';
import { exportXlsx } from '../reports/reportExport';
import { PageHeader } from '../../pages/AppShell';
import type { ExpenseClaim, ExpenseStatus } from './types';
import type { ReportColumn } from '../../types';
import { KIND_LABEL, rupees } from './types';

/**
 * Expense claims for a manager or admin.
 *
 * A manager sees the ones on their desk; an admin sees the company's.
 * Pending claims carry Approve / Reject (two-level routes a salesman's
 * claim through the manager first, then the admin). An approved claim an
 * admin marks Disbursed. The server decides who may act on what.
 */
const TABS: { value: ExpenseStatus; label: string }[] = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'disbursed', label: 'Disbursed' },
  { value: 'rejected', label: 'Rejected' },
];

const STATUS_TONE: Record<ExpenseStatus, 'warning' | 'success' | 'danger' | 'neutral' | 'brand'> = {
  pending: 'warning', approved: 'success', disbursed: 'brand', rejected: 'danger', cancelled: 'neutral',
};

const fmtDate = (s: string) => new Date(`${s}T12:00:00Z`)
  .toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' });

function stageLabel(c: ExpenseClaim): string {
  if (c.status !== 'pending') return c.status;
  return c.pendingLevel === 'manager' ? 'Awaiting manager' : 'Awaiting admin';
}

export default function ExpenseClaimsPage() {
  const { can } = useAuth();
  const toast = useToast();
  const isAdmin = can('company_admin', 'platform_admin');

  const [status, setStatus] = useState<ExpenseStatus>('pending');
  const [rows, setRows] = useState<ExpenseClaim[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState<ExpenseClaim | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<{ claims: ExpenseClaim[] }>('/expenses/claims/pending', { status });
      setRows(res.claims);
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not load claims.');
    } finally {
      setLoading(false);
    }
  }, [status, toast]);

  useEffect(() => { void load(); }, [load]);

  async function exportClaims() {
    if (rows.length === 0) return;
    const cols: ReportColumn[] = [
      { key: 'who', label: 'Who', group: '', type: 'text' },
      { key: 'category', label: 'Category', group: '', type: 'text' },
      { key: 'date', label: 'Date', group: '', type: 'text' },
      { key: 'amount', label: 'Amount (Rs)', group: '', type: 'number' },
      { key: 'km', label: 'KM', group: '', type: 'number' },
      { key: 'notes', label: 'Notes', group: '', type: 'text' },
      { key: 'status', label: 'Status', group: '', type: 'text' },
    ];
    const data = rows.map((r) => ({
      who: r.user.name,
      category: r.category.name ?? KIND_LABEL[r.kind],
      date: fmtDate(r.expenseDate),
      amount: Number(r.amount),
      km: r.km ?? '',
      notes: r.notes ?? '',
      status: stageLabel(r),
    }));
    await exportXlsx(`expenses_${status}`, cols, data);
  }

  return (
    <>
      <PageHeader
        title="Expense claims"
        subtitle="Approve, decline and disburse what your team claims."
        actions={(
          <button type="button" className="btn btn-ghost"
            disabled={rows.length === 0} onClick={() => void exportClaims()}>
            <Icon name="download" size={16} /> Export
          </button>
        )}
      />

      <div className="page col gap-16">
        <div className="tabs">
          {TABS.map((t) => (
            <button key={t.value} type="button"
              className={`tab${t.value === status ? ' active' : ''}`}
              onClick={() => setStatus(t.value)}>
              {t.label}
            </button>
          ))}
        </div>

        {loading ? <PageLoading /> : rows.length === 0 ? (
          <div className="card">
            <EmptyState icon="checklist"
              title={status === 'pending' ? 'Nothing to review' : `No ${status} claims`}
              body={status === 'pending' ? 'Claims your team files show up here.' : undefined} />
          </div>
        ) : (
          <div className="card">
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Who</th>
                    <th>Category</th>
                    <th>Date</th>
                    <th className="num">Amount</th>
                    <th>Details</th>
                    <th>Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => setOpen(r)}>
                      <td className="primary">{r.user.name}</td>
                      <td>
                        <span className="row gap-8" style={{ alignItems: 'center' }}>
                          {r.category.name ?? KIND_LABEL[r.kind]}
                          {r.kind !== 'general' ? <Badge tone="brand">{KIND_LABEL[r.kind]}</Badge> : null}
                        </span>
                      </td>
                      <td className="nowrap">{fmtDate(r.expenseDate)}</td>
                      <td className="num" style={{ fontWeight: 600 }}>{rupees(r.amount)}</td>
                      <td className="small faint nowrap">
                        {r.km != null ? `${r.km} km` : null}
                        {r.km != null && (r.photos.length || r.visitIds.length) ? ' · ' : null}
                        {r.photos.length ? `${r.photos.length} photo${r.photos.length === 1 ? '' : 's'}` : null}
                        {r.photos.length && r.visitIds.length ? ' · ' : null}
                        {r.visitIds.length ? `${r.visitIds.length} visit${r.visitIds.length === 1 ? '' : 's'}` : null}
                        {!r.km && !r.photos.length && !r.visitIds.length ? '—' : null}
                      </td>
                      <td><Badge tone={STATUS_TONE[r.status]}>{stageLabel(r)}</Badge></td>
                      <td>
                        <span className="small faint">View ›</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {open ? (
        <ClaimModal
          claim={open}
          isAdmin={isAdmin}
          onClose={() => setOpen(null)}
          onDone={() => { setOpen(null); void load(); }}
        />
      ) : null}
    </>
  );
}

type Action = 'approve' | 'reject' | 'disburse' | null;

function ClaimModal({ claim, isAdmin, onClose, onDone }: {
  claim: ExpenseClaim; isAdmin: boolean; onClose: () => void; onDone: () => void;
}) {
  const toast = useToast();
  const [action, setAction] = useState<Action>(null);
  const [note, setNote] = useState('');
  const [ref, setRef] = useState('');
  const [busy, setBusy] = useState(false);

  const canDecide = claim.status === 'pending';
  const canDisburse = claim.status === 'approved' && isAdmin;

  async function run() {
    setBusy(true);
    try {
      if (action === 'disburse') {
        await api.post(`/expenses/claims/${claim.id}/disburse`, { reference: ref.trim() || undefined });
        toast.success('Marked as disbursed.');
      } else if (action === 'approve' || action === 'reject') {
        await api.post(`/expenses/claims/${claim.id}/decision`, { decision: action, note: note.trim() || undefined });
        toast.success(action === 'approve' ? 'Claim approved.' : 'Claim declined.');
      }
      onDone();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not save that.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={`${claim.category.name ?? KIND_LABEL[claim.kind]} · ${rupees(claim.amount)}`}
      subtitle={`${claim.user.name} · ${fmtDate(claim.expenseDate)}`}
      onClose={onClose}
      footer={(
        <>
          <span className="grow" />
          <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
          {canDecide ? (
            <>
              <button type="button" className="btn btn-danger-ghost" disabled={busy}
                onClick={() => setAction(action === 'reject' ? null : 'reject')}>Reject</button>
              <button type="button" className="btn btn-primary" disabled={busy}
                onClick={() => setAction(action === 'approve' ? null : 'approve')}>Approve</button>
            </>
          ) : null}
          {canDisburse ? (
            <button type="button" className="btn btn-primary" disabled={busy}
              onClick={() => setAction(action === 'disburse' ? null : 'disburse')}>Mark disbursed</button>
          ) : null}
        </>
      )}
    >
      <div className="col gap-12">
        <dl className="kv" style={{ margin: 0 }}>
          <dt>Amount</dt><dd style={{ fontWeight: 600 }}>{rupees(claim.amount)}</dd>
          {claim.km != null ? (
            <>
              <dt>Distance</dt>
              <dd>
                {claim.km} km{claim.rate ? ` × ${rupees(claim.rate)}/km` : ''}
                {claim.kmSource ? ` · ${claim.kmSource === 'odometer' ? 'odometer' : 'GPS'}` : ''}
              </dd>
            </>
          ) : null}
          {claim.kind === 'daily_allowance' && claim.rate ? (
            <><dt>Rate</dt><dd>{rupees(claim.rate)} / day</dd></>
          ) : null}
          <dt>Status</dt><dd><Badge tone={STATUS_TONE[claim.status]}>{stageLabel(claim)}</Badge></dd>
          {claim.notes ? <><dt>Notes</dt><dd>{claim.notes}</dd></> : null}
          {claim.visitIds.length ? (
            <><dt>Visits</dt><dd>{claim.visitIds.length} attached from that day</dd></>
          ) : null}
          {claim.managerDecidedBy ? (
            <><dt>Manager</dt><dd>Approved by {claim.managerDecidedBy.name}</dd></>
          ) : null}
          {claim.decidedBy ? (
            <>
              <dt>{claim.status === 'rejected' ? 'Declined by' : 'Approved by'}</dt>
              <dd>{claim.decidedBy.name}{claim.decisionNote ? ` — “${claim.decisionNote}”` : ''}</dd>
            </>
          ) : null}
          {claim.disbursedBy ? (
            <>
              <dt>Disbursed</dt>
              <dd>by {claim.disbursedBy.name}{claim.disbursementRef ? ` · ${claim.disbursementRef}` : ''}</dd>
            </>
          ) : null}
        </dl>

        {claim.photos.length ? (
          <div className="col gap-4">
            <span className="small" style={{ fontWeight: 600 }}>Receipts</span>
            <div className="row gap-8" style={{ flexWrap: 'wrap' }}>
              {claim.photos.map((p, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <a key={i} href={p.url} target="_blank" rel="noreferrer">
                  <img src={p.url} alt={`Receipt ${i + 1}`}
                    style={{ width: 92, height: 92, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--border)' }} />
                </a>
              ))}
            </div>
          </div>
        ) : null}

        {action === 'approve' || action === 'reject' ? (
          <div className="col gap-8" style={{ borderTop: '1px solid var(--border)', paddingTop: 12 }}>
            <TextArea
              label={action === 'approve' ? 'Note (optional)' : 'Reason for declining (optional)'}
              rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
            <div className="row gap-8" style={{ justifyContent: 'flex-end' }}>
              <button type="button" className={`btn ${action === 'approve' ? 'btn-primary' : 'btn-danger'}`}
                disabled={busy} onClick={() => void run()}>
                {action === 'approve' ? 'Confirm approval' : 'Confirm decline'}
              </button>
            </div>
          </div>
        ) : null}

        {action === 'disburse' ? (
          <div className="col gap-8" style={{ borderTop: '1px solid var(--border)', paddingTop: 12 }}>
            <TextInput label="Payment reference (optional)" value={ref}
              placeholder="NEFT / UPI ref" onChange={(e) => setRef(e.target.value)} />
            <div className="row gap-8" style={{ justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void run()}>
                Confirm disbursed
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </Modal>
  );
}
